import { useState } from 'react'

import type { PatientWithOwners } from '@snootsvet/shared'
import { formatName } from '@snootsvet/shared'

import { QueryCombobox } from '@/frontend/components/query-combobox'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatients } from '@/frontend/hooks/api/patients/use-list-patients'

interface PatientSearchProps {
  id?: string
  /** Controlled selection — the caller owns the selected patient. */
  value?: PatientWithOwners | null
  /** Uncontrolled — seeds the selection on mount only. Ignored when `value` is provided. */
  initialValue?: PatientWithOwners | null
  /** Called with the patient ID when a selection is made, or null when cleared. */
  onChange: (patientId: string | null) => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  /** Show a clear button to deselect the current value. */
  showClear?: boolean
  'aria-invalid'?: boolean
}

function PatientSearch({
  onChange,
  placeholder = 'Search patients...',
  ...props
}: PatientSearchProps) {
  const { region } = useViewerRegion()
  const [search, setSearch] = useState((props.value ?? props.initialValue)?.name ?? '')
  const query = useListPatients({
    q: search || undefined,
    limit: 50,
    region,
    enabled: !!search && !props.disabled,
  })

  return (
    <QueryCombobox<PatientWithOwners, { patients: PatientWithOwners[] }>
      query={query}
      getItems={(data) => data?.patients ?? []}
      onSearchChange={setSearch}
      onValueChange={(patient) => onChange(patient ? patient.id : null)}
      isEqual={(a, b) => a.id === b.id}
      getKey={(patient) => patient.id}
      getLabel={(patient) => `${patient.name} · ${formatName(patient.owner)}`}
      placeholder={placeholder}
      emptyMessage="No patients found."
      renderItem={(patient) => (
        <div className="flex flex-col">
          <span>
            {patient.name}
            {' · '}
            {formatName(patient.owner)}
          </span>
          <span className="text-sm text-muted-foreground">
            {patient.species}
            {patient.breed ? ` · ${patient.breed}` : ''}
          </span>
        </div>
      )}
      {...props}
    />
  )
}

export { PatientSearch }
